import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, GraduationCap, Lock, Mail } from 'lucide-react';
import { ROLE_HOME_ROUTE } from '@tutor/shared';
import { loginSchema, type LoginInput } from '@tutor/validation';
import { Alert, Button, Input } from '@tutor/ui';
import { ApiError } from '../../lib/api-client';
import { useAuthStore } from '../../stores/auth.store';

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const user = await login(values);

      // Where the user goes next is decided by the role the *server* returned.
      const intended = (location.state as LocationState | null)?.from?.pathname;
      const home = ROLE_HOME_ROUTE[user.role] ?? '/';
      const destination = intended && intended !== '/login' ? intended : home;

      navigate(destination, { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.isValidation && error.details) {
          for (const [field, messages] of Object.entries(error.details)) {
            if (field === 'email' || field === 'password') {
              setError(field, { message: messages[0] });
            }
          }
        }
        setFormError(error.message);
        return;
      }
      setFormError('We could not reach the server. Please check your connection and try again.');
    }
  });

  return (
    <div className="flex min-h-screen">
      {/* Brand panel — decorative, so it is hidden rather than read out on small screens. */}
      <div className="relative hidden w-1/2 flex-col justify-between bg-brand-700 p-12 text-white lg:flex">
        <Link to="/" className="flex w-fit items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
            <GraduationCap className="h-5 w-5" aria-hidden />
          </span>
          <span className="text-lg font-semibold tracking-tight">Tutor Platform</span>
        </Link>

        <div className="max-w-md">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            Every lesson, every piece of homework, every step of progress — in one place.
          </h2>
          <p className="mt-4 text-brand-100">
            Live classrooms with a shared whiteboard, homework that marks itself into a
            progress record, and a schedule both sides can trust.
          </p>
        </div>

        <p className="text-sm text-brand-200">
          © {new Date().getFullYear()} Tutor Platform. All rights reserved.
        </p>
      </div>

      <div className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-8 block w-fit lg:hidden">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-600 text-white">
              <GraduationCap className="h-5 w-5" aria-hidden />
            </span>
          </Link>

          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Sign in</h1>
          <p className="mt-1.5 text-sm text-slate-500">
            Welcome back. Enter your details to continue.
          </p>

          {formError && (
            <Alert tone="danger" className="mt-6">
              {formError}
            </Alert>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              leftIcon={<Mail className="h-4 w-4" aria-hidden />}
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              leftIcon={<Lock className="h-4 w-4" aria-hidden />}
              error={errors.password?.message}
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="rounded p-1.5 text-slate-400 hover:text-slate-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
              {...register('password')}
            />

            <Button type="submit" fullWidth size="lg" isLoading={isSubmitting}>
              Sign In
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              Forgot Password?
            </Link>
          </div>

          <div className="mt-6 border-t border-slate-200 pt-6 text-center">
            <p className="text-sm text-slate-500">
              New to the platform?{' '}
              <Link to="/register" className="font-medium text-brand-600 hover:text-brand-700">
                Create an account
              </Link>
            </p>
          </div>

          {import.meta.env.DEV && (
            <div className="mt-10 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Development accounts
              </p>
              <dl className="mt-2 space-y-1 text-xs text-slate-600">
                <div className="flex justify-between gap-3">
                  <dt>Tutor</dt>
                  <dd className="font-mono">tutor@example.com · Tutor123!</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>Student</dt>
                  <dd className="font-mono">student@example.com · Student123!</dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
